/**
 * IL MODELLO DEL GIUDIZIO DEL COACH «Valuta le mie mosse» — modulo PURO (BACKGAMMON-COACH, tappa B, spec-20b §4).
 *
 * Nessun React, nessun i18n, nessun env, nessuno storage, nessuna rete: importa SOLO tipi di
 * `@qlashgeminiz/qgammon-types`. Rende CHIAVI e valori, non frasi: le frasi sono dei cataloghi (`*`)
 * e la lingua per i numeri la passa il chiamante. Per questo il web lo puo' ricopiare con `COPIA.json`
 * (spec-20b §4): gli import sono solo `@qlashgeminiz/*` e relativi, e non c'e' nulla di Multi5 o del valutatore
 * (ADR-0261): il client mostra solo cio' che il server manda (ADR-0266).
 *
 * ⛔ Il client NON giudica niente: legge `grade`, `loss`, `played`, `best` del verdetto. Nessuna soglia qui dentro
 * (le soglie sono del server, in `engine.unit`); nessuna parola che dica chi ha ragione: il giudizio e' «secondo il
 * motore QLASH» (ADR-0266 §7), e questo e' un compito dei testi.
 */
import type {
  CoachGrade,
  CoachHistoryPayload,
  CoachVerdictPayload,
} from '@qlashgeminiz/qgammon-types';

/** Un verdetto del server, cosi' com'e' nel tipo (la TMA lo parsa con lo schema prima di arrivare qui). */
export type VerdettoCoach = CoachVerdictPayload;
type Mossa = NonNullable<VerdettoCoach['played']>['moves'][number];

/* ------------------------------------------------------------------------------------------------------------- *
 * Lo stato di una riga e il suo grado
 * ------------------------------------------------------------------------------------------------------------- */

/**
 * Cosa mostra una riga. `ok` raccoglie le tre classi con la perdita (`in_linea`, `imprecisa`, `errore`): quale sia lo
 * dice `chiaveGrado`. `non_disponibile` e' un verdetto `unavailable` (o un `ok` senza il grado: non si inventa).
 */
export type StatoRiga = 'ok' | 'forzata' | 'nessuna_mossa' | 'equivalente' | 'non_disponibile';

export function statoRiga(v: VerdettoCoach): StatoRiga {
  if (v.status !== 'ok' || v.grade === undefined) return 'non_disponibile';
  switch (v.grade) {
    case 'forzata':
      return 'forzata';
    case 'nessuna_mossa':
      return 'nessuna_mossa';
    case 'equivalente':
      return 'equivalente';
    case 'in_linea':
    case 'imprecisa':
    case 'errore':
      return 'ok';
    default:
      // Un grado che un server futuro aggiungesse: mai un voto inventato.
      return 'non_disponibile';
  }
}

/** La chiave del catalogo per il nome del grado. Un grado sconosciuto non ha chiave: il chiamante non lo disegna. */
export function chiaveGrado(g: CoachGrade): string {
  return `grado.${g}`;
}

/** La chiave del commento breve di una classe (una frase per classe, sempre «secondo il motore QLASH»). */
export function chiaveCommento(g: CoachGrade): string {
  return `commento.${g}`;
}

/** La chiave del motivo di `unavailable`: `busy`, `timeout` o `error`; un motivo nuovo cade su `error`. */
export function chiaveNonDisponibile(reason: VerdettoCoach['reason']): string {
  return `nonDisponibile.${reason === 'busy' || reason === 'timeout' ? reason : 'error'}`;
}

/**
 * Il tono di un grado. NON e' un colore: il colore non e' mai l'unico portatore (spec §3), l'etichetta e l'icona ci sono
 * comunque. Il chiamante lo traduce in una classe dei token (`text-success`, `text-warning`, `text-danger`, `text-fg-muted`).
 */
export type TonoGrado = 'buono' | 'neutro' | 'avviso' | 'errore';

export function tonoGrado(g: CoachGrade): TonoGrado {
  switch (g) {
    case 'in_linea':
      return 'buono';
    case 'imprecisa':
      return 'avviso';
    case 'errore':
      return 'errore';
    default:
      return 'neutro';
  }
}

/** Il NOME di un'icona, non un import (questo modulo non conosce lucide). */
export type IconaGrado = 'check' | 'uguale' | 'attenzione' | 'errore' | 'uno' | 'nessuna';

export function iconaGrado(g: CoachGrade): IconaGrado {
  switch (g) {
    case 'in_linea':
      return 'check';
    case 'equivalente':
      return 'uguale';
    case 'imprecisa':
      return 'attenzione';
    case 'errore':
      return 'errore';
    case 'forzata':
      return 'uno';
    default:
      return 'nessuna';
  }
}

/* ------------------------------------------------------------------------------------------------------------- *
 * La perdita
 * ------------------------------------------------------------------------------------------------------------- */

const MENO = '−';

/** La chiave del modello di frase che mette il numero in unita' (`{{valore}} pp` / `{{valore}} equity`): il numero lo da' `formattaPerdita`. */
export function chiavePerdita(unit: VerdettoCoach['engine']['unit']): string {
  return `perdita.${unit}`;
}

/**
 * La perdita come numero leggibile, nell'unita' del verdetto, con la lingua che passa il chiamante (`Intl`: la virgola
 * giusta di it e en, e le altre due).
 *  - `winProb`: punti percentuali di vittoria, un decimale (0,023 → «−2,3»);
 *  - `equityCubeless`: equity a tre decimali (0,031 → «−0,031»).
 * Sempre col MENO vero (U+2212), perche' e' una perdita; tranne quando il numero, arrotondato, e' zero: un «−0,0»
 * direbbe una perdita che non c'e'. Un valore non finito o negativo non produce un numero: `null` (mai «NaN» a schermo).
 */
export function formattaPerdita(
  loss: number | undefined,
  unit: VerdettoCoach['engine']['unit'],
  locale: string,
): string | null {
  if (loss === undefined || !Number.isFinite(loss) || loss < 0) return null;
  const decimali = unit === 'winProb' ? 1 : 3;
  const valore = unit === 'winProb' ? loss * 100 : loss;
  let fmt: Intl.NumberFormat;
  try {
    fmt = new Intl.NumberFormat(locale, { minimumFractionDigits: decimali, maximumFractionDigits: decimali });
  } catch {
    fmt = new Intl.NumberFormat('en', { minimumFractionDigits: decimali, maximumFractionDigits: decimali });
  }
  const arrotondato = Number(valore.toFixed(decimali));
  const testo = fmt.format(Math.abs(valore));
  return arrotondato === 0 ? testo : `${MENO}${testo}`;
}

/**
 * Il testo del voto per chi non lo vede (l'`aria-label` della riga chiusa in orizzontale, dove la parola puo' non starci): la parola del grado e, se c'e', la perdita
 * nell'unita' del verdetto. `null` per un verdetto che non e' `ok`.
 */
export function testoGradoCoach(v: VerdettoCoach, t: (k: string, o?: Record<string, unknown>) => string, lingua: string): string | null {
  if (v.status !== 'ok' || v.grade === undefined) return null;
  const perdita = formattaPerdita(v.loss, v.engine.unit, lingua);
  const mostraPerdita = perdita !== null && v.grade !== 'forzata' && v.grade !== 'nessuna_mossa';
  return mostraPerdita ? `${t(chiaveGrado(v.grade))} ${t(chiavePerdita(v.engine.unit), { valore: perdita })}` : t(chiaveGrado(v.grade));
}

/* ------------------------------------------------------------------------------------------------------------- *
 * L'identita' di un verdetto e la storia
 * ------------------------------------------------------------------------------------------------------------- */

/** `matchId|gameNumber|ply|version`: la stessa chiave porta sempre lo stesso verdetto (un retry, una seconda consegna, la storia). */
export function chiaveVerdetto(v: Pick<VerdettoCoach, 'matchId' | 'gameNumber' | 'ply' | 'version'>): string {
  return `${v.matchId}|${v.gameNumber}|${v.ply}|${v.version}`;
}

function ordina(a: VerdettoCoach, b: VerdettoCoach): number {
  return a.gameNumber - b.gameNumber || a.ply - b.ply || (a.version < b.version ? -1 : a.version > b.version ? 1 : 0);
}

/**
 * Un verdetto che dice la stessa cosa di un altro sullo stesso turno ma disegnerebbe un ALTRO tavolo (stesso
 * matchId/game/ply/giocatore, `before` o dadi diversi): non si accetta (spec §5, «mai si disegna su un altro board»).
 */
function stessoTavolo(a: VerdettoCoach, b: VerdettoCoach): boolean {
  return (
    a.player === b.player &&
    a.dice[0] === b.dice[0] &&
    a.dice[1] === b.dice[1] &&
    a.before.points.length === b.before.points.length &&
    a.before.points.every((n, i) => n === b.before.points[i]) &&
    a.before.bar.W === b.before.bar.W &&
    a.before.bar.B === b.before.bar.B &&
    a.before.off.W === b.before.off.W &&
    a.before.off.B === b.before.off.B
  );
}

/**
 * `coach:history` SOSTITUISCE: la storia che arriva a ogni `match:join` e' la storia intera della partita, quindi chi
 * rimonta non accumula (niente doppioni, anche se il server ne mandasse due uguali). Si tengono solo i verdetti del
 * `matchId` della storia, nell'ordine di partita e di turno.
 */
export function sostituisciStoria(
  _prima: readonly VerdettoCoach[],
  history: Pick<CoachHistoryPayload, 'matchId' | 'verdicts'>,
): VerdettoCoach[] {
  const viste = new Set<string>();
  const out: VerdettoCoach[] = [];
  for (const v of history.verdicts) {
    if (v.matchId !== history.matchId) continue;
    const k = chiaveVerdetto(v);
    if (viste.has(k)) continue;
    viste.add(k);
    out.push(v);
  }
  return out.sort(ordina);
}

/**
 * Aggiunge UN verdetto (`coach:verdict`). Idempotente sulla chiave: lo stesso verdetto due volte e' la stessa lista (lo
 * STESSO array, cosi' React non ridisegna). Un verdetto che contraddice la riga gia' presente per quel turno (stesso
 * turno, un altro tavolo di partenza) si scarta.
 */
export function inserisciVerdetto(storia: readonly VerdettoCoach[], v: VerdettoCoach): readonly VerdettoCoach[] {
  const k = chiaveVerdetto(v);
  for (const r of storia) {
    if (chiaveVerdetto(r) === k) return storia;
    if (r.matchId === v.matchId && r.gameNumber === v.gameNumber && r.ply === v.ply && !stessoTavolo(r, v)) return storia;
  }
  return [...storia, v].sort(ordina);
}

/* ------------------------------------------------------------------------------------------------------------- *
 * Chi e' il verdetto: la partita a schermo
 * ------------------------------------------------------------------------------------------------------------- */

/** Cio' che la TMA sa della partita a schermo, e basta. */
export interface VistaPartita {
  matchId: string;
  /** Il numero della partita (del match a punti) a schermo. */
  gameNumber: number;
}

/**
 * Un verdetto si accetta SOLO se e' della partita attiva e di una game non successiva a quella a schermo (spec §5): un
 * giudizio tardivo, o di un altro tavolo, non entra e non disegna niente.
 */
export function riferimentoCoincide(v: Pick<VerdettoCoach, 'matchId' | 'gameNumber'>, vista: VistaPartita | null): boolean {
  if (!vista) return false;
  return v.matchId === vista.matchId && v.gameNumber <= vista.gameNumber;
}

/* ------------------------------------------------------------------------------------------------------------- *
 * L'anteprima: quando l'icona della mossa suggerita c'e'
 * ------------------------------------------------------------------------------------------------------------- */

/** Un tavolo di riferimento, come lo vuole la funzione pura di board-ui (`PreviewPosition`). */
export interface PosizioneRif {
  readonly points: readonly number[];
  readonly bar: { readonly W: number; readonly B: number };
  readonly off: { readonly W: number; readonly B: number };
}

/** La funzione di validazione dell'anteprima che la TMA passa (`planMovePreview` di board-ui 0.17.0): questo modulo non importa board-ui. */
export type PianificaAnteprima = (
  position: PosizioneRif,
  player: VerdettoCoach['player'],
  moves: readonly Mossa[],
) => { readonly ok: boolean };

/**
 * L'icona della mossa suggerita c'e' solo se il verdetto e' `ok`, ha un grado CON una scelta (non `forzata`, non
 * `nessuna_mossa`: non c'e' niente da suggerire), ha `best` con almeno una mossa e — se il chiamante passa la funzione
 * pura di board-ui — la sequenza e' applicabile al tavolo di riferimento. Un `unavailable` non l'ha mai, nemmeno spenta.
 * Cosi' non si mostra un'icona che poi si spegne (condizione 5 di tma-orcr).
 */
export function anteprimaApribile(v: VerdettoCoach, pianifica?: PianificaAnteprima): boolean {
  if (statoRiga(v) === 'non_disponibile') return false;
  if (v.grade === 'forzata' || v.grade === 'nessuna_mossa') return false;
  const moves = v.best?.moves;
  if (!moves || moves.length === 0) return false;
  if (!pianifica) return true;
  try {
    return pianifica(v.before, v.player, moves).ok === true;
  } catch {
    return false;
  }
}

/** La stessa cosa per il tono «La tua mossa»: serve `played` con almeno una mossa. */
export function anteprimaGiocataApribile(v: VerdettoCoach, pianifica?: PianificaAnteprima): boolean {
  if (statoRiga(v) === 'non_disponibile') return false;
  const moves = v.played?.moves;
  if (!moves || moves.length === 0) return false;
  if (!pianifica) return true;
  try {
    return pianifica(v.before, v.player, moves).ok === true;
  } catch {
    return false;
  }
}

/** Il tavolo dopo una sequenza, come testo confrontabile; `null` se la sequenza non e' applicabile (mai un'eccezione). */
function posizioneDopo(v: VerdettoCoach, moves: readonly Mossa[]): string | null {
  const points = [...v.before.points];
  const bar = { ...v.before.bar };
  const off = { ...v.before.off };
  const me = v.player;
  const altro = me === 'W' ? 'B' : 'W';
  const segno = me === 'W' ? 1 : -1;
  for (const m of moves) {
    if (m.from === 'bar') {
      if (bar[me] <= 0) return null;
      bar[me] -= 1;
    } else {
      if (!(m.from >= 0 && m.from < points.length)) return null;
      if (Math.sign(points[m.from]) !== segno) return null;
      points[m.from] -= segno;
    }
    if (m.to === 'off') {
      off[me] += 1;
    } else {
      if (!(m.to >= 0 && m.to < points.length)) return null;
      const presente = points[m.to];
      if (Math.sign(presente) === -segno) {
        if (Math.abs(presente) > 1) return null;
        points[m.to] = 0;
        bar[altro] += 1;
      }
      points[m.to] += segno;
    }
  }
  return JSON.stringify([points, bar.W, bar.B, off.W, off.B]);
}

/**
 * `played` e `best` portano alla STESSA posizione: allora il selettore dei due toni non c'e' (la riga resta della stessa
 * altezza, spec §5). Un ordine diverso delle stesse mosse e' la stessa posizione. Se una delle due sequenze non si
 * riesce a simulare, NON si dice «stessa»: il selettore resta (il caso di dubbio e' mostrare entrambe).
 */
export function stessaPosizione(v: VerdettoCoach): boolean {
  if (!v.played || !v.best) return false;
  const a = posizioneDopo(v, v.played.moves);
  const b = posizioneDopo(v, v.best.moves);
  return a !== null && b !== null && a === b;
}
